import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardCheck,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { AuditType, CreateAuditTypeData } from "../../../services/auditTypeService";
import Button from "../../../shared/components/ui/button/Button";

const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
  accent: "#A3CD39",
  dark: "#007F66",
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

interface Props {
  onAdd: (auditTypeData: CreateAuditTypeData) => Promise<void>;
  onClose?: () => void;
  editingAuditType?: AuditType | null;
  showSuccessAndClose?: boolean;
}

const schema = yup.object({
  name: yup.string().required("Name is required").trim().max(200, "Name must be less than 200 characters"),
  description: yup.string().optional().trim().max(500, "Description must be less than 500 characters"),
});

interface FormValues {
  name: string;
  description?: string;
}

export default function AuditTypeForm({ onAdd, onClose, editingAuditType, showSuccessAndClose = true }: Props) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const isEditMode = Boolean(editingAuditType);

  const getDefaultValues = (): Partial<FormValues> => {
    if (editingAuditType) {
      return {
        name: editingAuditType.name || '',
        description: editingAuditType.description || '',
      };
    }
    return {
      name: '',
      description: '',
    };
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: getDefaultValues(),
    shouldUnregister: false
  });

  const watchedName = watch("name");

  const onSubmit = async (data: FormValues) => {
    setApiError(null);
    setShowSuccess(false);

    try {
      const auditTypeData: CreateAuditTypeData = {
        name: data.name.trim(),
        description: data.description?.trim() || undefined,
      };

      await onAdd(auditTypeData);

      if (showSuccessAndClose) {
        setShowSuccess(true);
        toast.success(
          isEditMode
            ? "Audit Type updated successfully!"
            : "Audit Type created successfully!"
        );

        setTimeout(() => {
          onClose?.();
          reset();
        }, 1500);
      } else {
        reset();
      }
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>;
      let message = error.response?.data?.message || error.message || "Failed to save Audit Type. Please try again.";

      if (message.includes("duplicate key error") || message.includes("already exists")) {
        if (!message.includes("already exists")) {
          message = "An Audit Type with the same name already exists. Please use a different name.";
        }
      } else if (message.includes("validation failed")) {
        message = "Please check all required fields and try again.";
      }

      setApiError(message);
      console.error("Audit Type Save Error:", error);
    }
  };

  const formTitle = isEditMode ? "Edit Audit Type" : "Add New Audit Type";

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col"
    >
      {/* Success Message */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg border mb-4"
            style={{
              backgroundColor: BRAND_COLORS.successLight,
              borderColor: `${BRAND_COLORS.success}40`
            }}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.success + '20',
                borderColor: BRAND_COLORS.success + '40'
              }}
            >
              <CheckCircle2 className="w-5 h-5" style={{ color: BRAND_COLORS.success }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.success }}>
                Audit Type {isEditMode ? 'updated' : 'created'} successfully!
              </p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.success }}>
                {isEditMode ? 'Audit Type information has been updated.' : 'Audit Type has been added to the system.'}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Alert */}
      <AnimatePresence>
        {apiError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg border mb-4"
            style={{
              backgroundColor: BRAND_COLORS.errorLight,
              borderColor: `${BRAND_COLORS.error}40`
            }}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.error + '20',
                borderColor: BRAND_COLORS.error + '40'
              }}
            >
              <AlertTriangle className="w-5 h-5" style={{ color: BRAND_COLORS.error }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.error }}>Error</p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.error }}>{apiError}</p>
            </div>
            <button
              onClick={() => setApiError(null)}
              className="p-1.5 rounded-lg hover:bg-red-100 transition-colors"
              style={{ color: BRAND_COLORS.error }}
            >
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form Header */}
      <div className="flex items-center gap-3 pb-5 border-b border-gray-200 mb-6">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center border"
          style={{
            background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
            borderColor: BRAND_COLORS.primary
          }}
        >
          <ClipboardCheck className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900">{formTitle}</h2>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
            {isEditMode
              ? "Update Audit Type information"
              : "Create a new Audit Type"
            }
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X size={20} className="text-gray-500" />
        </button>
      </div>

      {/* Form Body - Scrollable */}
      <div className="flex-1 pr-2 overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-6 bg-blue-500 rounded-full"></div>
              <h3 className="text-base font-semibold text-gray-900">Audit Type Information</h3>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-900">
                Audit Type name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="name"
                  type="text"
                  {...register("name")}
                  className={`h-10 w-full rounded-lg border bg-white pl-3 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.name
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedName && !errors.name
                      ? `focus:ring-opacity-20`
                      : "border-gray-300 hover:border-gray-400"
                    }`}
                  style={watchedName && !errors.name ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.name ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="Enter audit type name"
                />
                {watchedName && !errors.name && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    style={{ color: BRAND_COLORS.primary }} />
                )}
                {errors.name && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                )}
              </div>
              {errors.name && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-900">
                Description
              </label>
              <textarea
                id="description"
                {...register("description")}
                rows={3}
                className={`w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.description
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 hover:border-gray-400"
                  }`}
                style={!errors.description ? {
                  '--tw-ring-color': BRAND_COLORS.primary + '40'
                } as React.CSSProperties : {}}
                placeholder="Enter optional description"
              />
              {errors.description && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.description.message}
                </p>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !watchedName}
              className="px-8 min-w-[140px]"
              style={{ backgroundColor: BRAND_COLORS.primary }}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                </div>
              ) : (
                <span>{isEditMode ? 'Update Audit Type' : 'Add Audit Type'}</span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
